import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SavedActivity {
  id: string;
  name: string;
  activityType?: string;
  savedAt: Date;
  data?: Record<string, any>;
}

interface ActivitiesState {
  activities: SavedActivity[];
  currentActivityId: string | null;
  addActivity: (activity: SavedActivity) => void;
  updateActivity: (id: string, updates: Partial<SavedActivity>) => void;
  removeActivity: (id: string) => void;
  deleteActivity: (id: string) => void;
  clearAllActivities: () => void;
  setCurrentActivityId: (id: string | null) => void;
  getActivityById: (id: string) => SavedActivity | undefined;
}

export const useActivitiesStore = create<ActivitiesState>()(
  persist(
    (set, get) => ({
      activities: [],
      currentActivityId: null,
      addActivity: (activity) =>
        set((state) => ({
          activities: [activity, ...state.activities],
          currentActivityId: activity.id,
        })),
      updateActivity: (id, updates) =>
        set((state) => ({
          activities: state.activities.map((a) =>
            a.id === id ? { ...a, ...updates } : a
          ),
        })),
      removeActivity: (id) =>
        set((state) => ({
          activities: state.activities.filter((a) => a.id !== id),
          currentActivityId:
            state.currentActivityId === id ? null : state.currentActivityId,
        })),
      deleteActivity: (id) =>
        set((state) => ({
          activities: state.activities.filter((a) => a.id !== id),
          currentActivityId:
            state.currentActivityId === id ? null : state.currentActivityId,
        })),
      clearAllActivities: () =>
        set({ activities: [], currentActivityId: null }),
      setCurrentActivityId: (id) => set({ currentActivityId: id }),
      getActivityById: (id) => get().activities.find((a) => a.id === id),
    }),
    {
      name: 'myfinance-activities-storage',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const parsed = JSON.parse(str);
          // Deserialize Date objects
          if (parsed.state?.activities) {
            parsed.state.activities = parsed.state.activities.map((a: any) => ({
              ...a,
              savedAt: new Date(a.savedAt),
            }));
          }
          return parsed;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
